import { createAsyncThunk } from '@reduxjs/toolkit';

import { portfolioService } from '../../services/portfolio';
import {
  Account,
  AccountCreationData,
  Holding,
  HoldingAnalysis,
  HoldingEntryData,
  PortfolioSummary,
  SecurityInfo,
} from '../../types';

/**
 * Shared portfolio async thunks
 * These work across both web and mobile platforms since they use the shared portfolioService
 */

export const fetchAccounts = createAsyncThunk<
  Account[],
  string,
  { rejectValue: { code: string; message: string } }
>('portfolio/fetchAccounts', async (userId, { rejectWithValue }) => {
  try {
    const result = await portfolioService.getAccountsByUser(userId);
    if (result.error) {
      return rejectWithValue({
        code: result.error.code,
        message: result.error.message,
      });
    }
    return result.accounts;
  } catch {
    return rejectWithValue({
      code: 'UNKNOWN_ERROR',
      message: 'Failed to fetch accounts',
    });
  }
});

export const createAccount = createAsyncThunk<
  Account,
  { userId: string; accountData: AccountCreationData },
  { rejectValue: { code: string; message: string } }
>(
  'portfolio/createAccount',
  async ({ userId, accountData }, { rejectWithValue }) => {
    try {
      const result = await portfolioService.createAccount(userId, accountData);
      if (result.error) {
        return rejectWithValue({
          code: result.error.code,
          message: result.error.message,
        });
      }
      if (!result.account) {
        return rejectWithValue({
          code: 'UNKNOWN_ERROR',
          message: 'Account creation failed',
        });
      }
      return result.account;
    } catch {
      return rejectWithValue({
        code: 'UNKNOWN_ERROR',
        message: 'Failed to create account',
      });
    }
  }
);

export const fetchHoldings = createAsyncThunk<
  HoldingAnalysis[],
  string,
  { rejectValue: { code: string; message: string } }
>('portfolio/fetchHoldings', async (userId, { rejectWithValue }) => {
  try {
    const result = await portfolioService.getHoldingAnalysis(userId);
    if (result.error) {
      return rejectWithValue({
        code: result.error.code,
        message: result.error.message,
      });
    }
    return result.analysis;
  } catch {
    return rejectWithValue({
      code: 'UNKNOWN_ERROR',
      message: 'Failed to fetch holdings',
    });
  }
});

export const createHolding = createAsyncThunk<
  Holding,
  { accountId: string; holdingData: HoldingEntryData },
  { rejectValue: { code: string; message: string } }
>(
  'portfolio/createHolding',
  async ({ accountId, holdingData }, { rejectWithValue }) => {
    try {
      const result = await portfolioService.createHolding(
        accountId,
        holdingData
      );
      if (result.error) {
        return rejectWithValue({
          code: result.error.code,
          message: result.error.message,
        });
      }
      if (!result.holding) {
        return rejectWithValue({
          code: 'UNKNOWN_ERROR',
          message: 'Holding creation failed',
        });
      }
      return result.holding;
    } catch {
      return rejectWithValue({
        code: 'UNKNOWN_ERROR',
        message: 'Failed to create holding',
      });
    }
  }
);

export const updateHolding = createAsyncThunk<
  Holding,
  { holdingId: string; updates: Partial<HoldingEntryData> },
  { rejectValue: { code: string; message: string } }
>(
  'portfolio/updateHolding',
  async ({ holdingId, updates }, { rejectWithValue }) => {
    try {
      const result = await portfolioService.updateHolding(holdingId, updates);
      if (result.error) {
        return rejectWithValue({
          code: result.error.code,
          message: result.error.message,
        });
      }
      if (!result.holding) {
        return rejectWithValue({
          code: 'UNKNOWN_ERROR',
          message: 'Holding update failed',
        });
      }
      return result.holding;
    } catch {
      return rejectWithValue({
        code: 'UNKNOWN_ERROR',
        message: 'Failed to update holding',
      });
    }
  }
);

export const deleteHolding = createAsyncThunk<
  string,
  string,
  { rejectValue: { code: string; message: string } }
>('portfolio/deleteHolding', async (holdingId, { rejectWithValue }) => {
  try {
    const result = await portfolioService.deleteHolding(holdingId);
    if (result.error) {
      return rejectWithValue({
        code: result.error.code,
        message: result.error.message,
      });
    }
    return holdingId;
  } catch {
    return rejectWithValue({
      code: 'UNKNOWN_ERROR',
      message: 'Failed to delete holding',
    });
  }
});

export const fetchPortfolioSummary = createAsyncThunk<
  PortfolioSummary,
  string,
  { rejectValue: { code: string; message: string } }
>('portfolio/fetchPortfolioSummary', async (userId, { rejectWithValue }) => {
  try {
    const result = await portfolioService.calculatePortfolioSummary(userId);
    if (result.error) {
      return rejectWithValue({
        code: result.error.code,
        message: result.error.message,
      });
    }
    if (!result.summary) {
      return rejectWithValue({
        code: 'UNKNOWN_ERROR',
        message: 'Portfolio summary calculation failed',
      });
    }
    return result.summary;
  } catch {
    return rejectWithValue({
      code: 'UNKNOWN_ERROR',
      message: 'Failed to fetch portfolio summary',
    });
  }
});

export const validateTicker = createAsyncThunk<
  { ticker: string; isValid: boolean; security?: SecurityInfo; error?: string },
  string,
  { rejectValue: { code: string; message: string } }
>('portfolio/validateTicker', async (ticker, { rejectWithValue }) => {
  try {
    const result = await portfolioService.validateTicker(ticker);
    return {
      ticker,
      isValid: result.isValid,
      security: result.security,
      error: result.error,
    };
  } catch {
    return rejectWithValue({
      code: 'UNKNOWN_ERROR',
      message: 'Failed to validate ticker',
    });
  }
});
